import { fmtNumber } from "@/lib/format";

function StatBox({ label, value, tone = "text-stone-900", testId }) {
  return (
    <div data-testid={testId} className="rounded-lg border border-stone-200 bg-stone-50/60 px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">{label}</div>
      <div className={`mt-0.5 font-mono text-sm font-bold ${tone}`}>{value}</div>
    </div>
  );
}

function flattenTree(node, depth, out) {
  out.push({ node, depth });
  (node.children || []).forEach((c) => flattenTree(c, depth + 1, out));
}

export function ExplodeNode({ node }) {
  const flat = [];
  flattenTree(node, 0, flat);
  return (
    <div data-testid={`explode-node-${node.productId || node.name}`}>
      {flat.map(({ node: n, depth }, ni) => (
        <div key={ni} style={{ marginLeft: depth * 16 }} className={depth > 0 ? "border-l border-stone-200 pl-3" : ""}>
          <div className="flex items-center gap-2 py-1">
            <span className={`text-sm ${depth === 0 ? "font-bold text-stone-900" : "font-semibold text-violet-700"}`}>{n.name}</span>
            <span className="font-mono text-xs text-stone-500">× {Number(n.qty).toFixed(3)} {n.unit}</span>
            {depth > 0 && <span className="rounded bg-violet-100 px-1.5 text-[9px] font-semibold uppercase text-violet-700">sub-assembly</span>}
            {n.missing && <span className="rounded bg-rose-100 px-1.5 text-[9px] font-semibold uppercase text-rose-700">missing product</span>}
          </div>
          {(n.raw || []).map((r, i) => (
            <div key={i} className="ml-4 flex items-center gap-2 py-0.5 text-xs text-stone-600">
              <span className="text-stone-300">-</span><span>{r.name}</span>
              <span className="font-mono text-stone-500">× {Number(r.qty).toFixed(3)} {r.unit}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export function SubAssembliesPanel({ subAssy }) {
  return (
    <div className="space-y-3" data-testid="plan-tab-subassemblies-panel">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Sub-Assembly Requirements (semi/finished products consumed to build these products)</div>
      {subAssy === null ? (
        <p className="rounded-xl border border-stone-200 bg-white px-4 py-8 text-center text-sm text-muted-foreground">Loading…</p>
      ) : subAssy.length === 0 ? (
        <p className="rounded-xl border border-stone-200 bg-white px-4 py-8 text-center text-sm text-muted-foreground" data-testid="plan-no-subassemblies">No sub-assemblies — these products are built directly from raw materials.</p>
      ) : subAssy.map((s) => (
        <div key={s.sku} data-testid={`plan-subassy-${s.sku}`} className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-display text-sm font-bold uppercase text-stone-900">{s.name}</div>
              <div className="mt-0.5 text-xs text-stone-500">{s.productId} · {s.sku}</div>
            </div>
            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${s.status === "SHORT" ? "border-rose-300 bg-rose-50 text-rose-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>{s.status}</span>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <StatBox label="Required" value={`${fmtNumber(s.qty, 3)} ${s.unit}`} testId={`plan-subassy-req-${s.sku}`} />
            <StatBox label="Available" value={`${fmtNumber(s.available, 3)} ${s.unit}`} testId={`plan-subassy-avail-${s.sku}`} />
            <StatBox label="Short" value={`${fmtNumber(s.short, 3)} ${s.unit}`} tone={s.short > 0 ? "text-rose-600" : "text-emerald-600"} testId={`plan-subassy-short-${s.sku}`} />
          </div>
          {s.short > 0 && <div className="mt-2 text-[11px] text-rose-700">Produce this sub-assembly first — production completion will be blocked without a Super-Admin override.</div>}
        </div>
      ))}
    </div>
  );
}

export function ExplodePanel({ explosion }) {
  if (explosion === null) return <p className="rounded-xl border border-stone-200 bg-white px-4 py-8 text-center text-sm text-muted-foreground">Loading…</p>;
  if (explosion.error) return <p className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-8 text-center text-sm text-rose-700" data-testid="plan-explode-error">{explosion.error}</p>;
  return (
    <div className="space-y-4" data-testid="plan-tab-explode-panel">
      {(explosion.trees || []).map((t, i) => <ExplodeNode key={i} node={t} />)}
      <div className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Total Raw Materials (fully exploded, theoretical)</div>
      <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white shadow-sm">
        <table className="w-full text-sm" data-testid="plan-explode-raw-table">
          <thead className="bg-stone-50 text-left text-[11px] uppercase text-stone-500">
            <tr>{["Ingredient", "ID", "Total Qty", "Available", "Net Need", "Packs", "Suggested Purchase"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {(explosion.rawMaterials || []).map((r) => (
              <tr key={r.ingredientId || r.name} data-testid={`plan-explode-raw-${r.ingredientCode || r.ingredientId}`}>
                <td className="px-3 py-2 font-medium text-stone-800">{r.name}</td>
                <td className="px-3 py-2 font-mono text-xs text-stone-500">{r.ingredientCode || "—"}</td>
                <td className="px-3 py-2 font-mono">{fmtNumber(r.qty, 3)} {r.unit}</td>
                <td className="px-3 py-2 font-mono text-stone-600">{fmtNumber(r.available, 3)} {r.unit}</td>
                <td className={`px-3 py-2 font-mono ${r.netNeed > 0 ? "text-rose-600" : "text-emerald-600"}`}>{fmtNumber(r.netNeed, 3)} {r.unit}</td>
                <td className="px-3 py-2 font-mono text-stone-600">{r.packSize > 0 ? `${Math.ceil((r.suggestedPurchase || 0) / r.packSize)} × ${fmtNumber(r.packSize, 3)} ${r.packUnit}` : "—"}</td>
                <td className={`px-3 py-2 font-mono ${r.netNeed > 0 ? "text-amber-700" : "text-emerald-600"}`}>{r.netNeed > 0 ? `${fmtNumber(r.suggestedPurchase, 3)} ${r.packSize > 0 ? r.packUnit : r.unit}` : "0"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
